import { Topic } from '../types/metadata';
import type { Hub } from '../types/hub';
import type { Program, ProgramHub } from '../types/program';

/** How many derived topic tags to keep when a place has no curated interests. */
export const DERIVED_INTEREST_TOPIC_LIMIT = 5;

const INTEREST_TOPIC_VALUES = new Set<string>(
  Object.values(Topic).filter((topic) => topic !== Topic.Unknown && topic !== Topic.Other)
);

export function isInterestTopic(value: string): value is Topic {
  return INTEREST_TOPIC_VALUES.has(value);
}

export function curatedInterestTopics(curated: string[] | undefined | null): Topic[] {
  if (!curated?.length) return [];
  const topics: Topic[] = [];
  for (const item of curated) {
    if (!isInterestTopic(item) || topics.includes(item)) continue;
    topics.push(item);
  }
  return topics;
}

/** Rank topic tags by how often they appear across contained resources. */
export function topTopicsByFrequency(
  topicLists: string[][],
  limit = DERIVED_INTEREST_TOPIC_LIMIT
): Topic[] {
  const counts = new Map<string, number>();
  for (const list of topicLists) {
    const seen = new Set<string>();
    for (const raw of list) {
      if (!isInterestTopic(raw) || seen.has(raw)) continue;
      seen.add(raw);
      counts.set(raw, (counts.get(raw) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([topic]) => topic as Topic);
}

/**
 * Curated interests win. When unset, derive the top tags from contained resources.
 * Does not write back to the place record.
 */
export function effectiveInterestTopics(
  curated: string[] | undefined | null,
  resourceTopicLists: string[][],
  limit = DERIVED_INTEREST_TOPIC_LIMIT
): Topic[] {
  const fromStaff = curatedInterestTopics(curated);
  if (fromStaff.length > 0) return fromStaff;
  return topTopicsByFrequency(resourceTopicLists, limit);
}

export function programResourceWpIds(program: Program): number[] {
  return [
    ...new Set([
      ...program.hubs.flatMap((hub) => hub.resources.map((resource) => resource.wpPostId)),
      ...program.highlights.map((highlight) => highlight.wpPostId),
    ]),
  ];
}

export function hubResourceWpIds(hub: Pick<Hub, 'resources'> | Pick<ProgramHub, 'resources'>): number[] {
  return [...new Set(hub.resources.map((resource) => resource.wpPostId))];
}

export function topicListsForWpIds(
  wpIds: number[],
  topicsByWpId: Map<number, string[]>
): string[][] {
  return wpIds.map((id) => topicsByWpId.get(id) ?? []);
}

export function placeNeedsDerivedInterests(
  curated: string[] | undefined | null,
  resourceWpIds: number[]
): boolean {
  return curatedInterestTopics(curated).length === 0 && resourceWpIds.length > 0;
}
